import { useEffect, useState } from 'react';
import SendTrophyBadge from './SendTrophyBadge';
import { UploadClimbVideoDialog } from './UploadClimbVideoDialog';
import { logDateFloor } from '../climb-log/log-date-floor';
import type { Climb } from '../../types/climb';
import type { ClimbLog } from '../../types/climb-log';

/**
 * What a climber does to a route from a list: the three things they log, plus
 * looking back at the goes they have already logged on it. `edit_send` is the
 * send form again, opened on a send already logged rather than on a blank one.
 */
export type ClimbLogActionKind = 'send' | 'edit_send' | 'try' | 'video' | 'attempts';

export interface ClimbLogAction {
  kind: ClimbLogActionKind;
  /**
   * Bumped on every request, so asking for the same action twice in a row
   * reopens its dialog rather than looking like nothing happened.
   */
  nonce: number;
}

interface ClimbLogActionsProps {
  climb: Climb;
  /** What to open. Null (or a stale nonce) leaves everything closed. */
  action: ClimbLogAction | null;
  /** Location slug, used to scope the send-date quick picker preference. */
  dateScope?: string | null;
  /** Fired after anything is logged, so the caller can refresh its list. */
  onLogged?: () => void;
  /**
   * The reader's log on this climb, whenever it is known — on load and after
   * every save — for a caller that shows their standing on the row itself and
   * wants it right without a refetch.
   */
  onLogChange?: (log: ClimbLog) => void;
}

/**
 * The dialogs behind "I sent it" / "I had a few goes" / "here is the video" /
 * "what have I done on this", with no trigger of its own — the caller owns the
 * button and says which one to open.
 *
 * The first two are `SendTrophyBadge`'s, driven through its external triggers
 * with its own badge hidden: the send dialog, the tries wheel and the confetti
 * are the same ones the climb profile puts behind its FAB, and a crag's topo
 * should not grow a second, subtly different set.
 *
 * Mounted ahead of time where the caller can (the crag topo keeps it up for
 * as long as a route is chosen), so the climb's ascent log is already loaded
 * when a dialog opens; where it cannot (a list row mounts it on the tap), the
 * badge holds the request until the log lands.
 */
export function ClimbLogActions({ climb, action, dateScope, onLogged, onLogChange }: ClimbLogActionsProps) {
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    if (action?.kind === 'video') setVideoOpen(true);
  }, [action]);

  return (
    <>
      <SendTrophyBadge
        hideBadge
        climbSlug={climb.slug}
        difficulty={climb.difficulty}
        minDate={logDateFloor(climb)}
        climbType={climb.type === 'Route' ? 'Route' : 'Boulder'}
        dateScope={dateScope}
        // Zero while another action is up: the badge only acts on a change to a
        // non-zero value, so switching away and back fires again.
        addTopTrigger={action?.kind === 'send' ? action.nonce : 0}
        editSendTrigger={action?.kind === 'edit_send' ? action.nonce : 0}
        addTryTrigger={action?.kind === 'try' ? action.nonce : 0}
        openAttemptsTrigger={action?.kind === 'attempts' ? action.nonce : 0}
        onSendsRefresh={onLogged}
        onRatingSaved={onLogged}
        // Null is "not loaded yet", not "no log": nothing to tell yet.
        onClimbLogChange={(log) => { if (log) onLogChange?.(log); }}
      />

      <UploadClimbVideoDialog
        open={videoOpen}
        onOpenChange={setVideoOpen}
        climbSlug={climb.slug}
        onUploaded={() => onLogged?.()}
      />
    </>
  );
}
